import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ActionPathField from './ActionPathField';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { normalizePathValue } from '../../Utils/pathUtils';

const DEFAULT_FOLDER_TARGET = 'C:\\Output\\{year}\\{month}\\Project_{projectid}';

const ActionsCreateFolder = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  initialScope = 'event',
}) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState(DEFAULT_FOLDER_TARGET);
  const [browsingDisabled, setBrowsingDisabled] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setTarget(folderPath);
      }
      setBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setBrowsingDisabled(false);
    },
  });

  const actionSettings = useActionSettings({
    settingId,
    caseid,
    actionType: 'createfolder',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      normalizePathValue(target) === normalizePathValue(initial.target)
    ),
    customSaveData: () => ({ target: normalizePathValue(target) }),
    loadDataFunction: (data) => {
      const storedTarget = normalizePathValue(data.customData?.target);
      setTarget(storedTarget || DEFAULT_FOLDER_TARGET);
    },
  });

  const targetMissing = !target.trim();

  const handleBrowseClick = useCallback(() => {
    setBrowsingDisabled(true);
    handleBrowseDirectory();
  }, [handleBrowseDirectory]);

  return (
    <>
      <ActionSettingsLayout
        title={t('auto_create_folder.auto_create_folders', 'Create Folders')}
        isEnabled={actionSettings.isEnabled}
        onEnabledChange={actionSettings.setIsEnabled}
        isSaveDisabled={actionSettings.isSaveDisabled}
        hasValidationError={targetMissing}
        onSave={actionSettings.handleSave}
        onForceRun={actionSettings.handleForceRun}
        forceRunLoading={actionSettings.forceRunLoading}
        conditions={actionSettings.conditions}
        onConditionsChange={actionSettings.handleConditionsChange}
        popUpOpen={actionSettings.popUpOpen}
        forceRunResult={actionSettings.forceRunResult}
        onCloseStatusWindow={actionSettings.handleCloseStatusWindow}
        tags={tags}
        primaryColor="#5757b0"
      >
        <Grid item xs={12}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title={t('auto_create_folder.target_title', 'Folder destination')}
              titleTypographyProps={{
                sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 },
              }}
              sx={{ p: { xs: 1.5, sm: 2 } }}
            />
            <CardContent>
              <ActionPathField
                browseDisabled={browsingDisabled}
                browseLabel={t('auto_create_folder.browse', 'Browse folder')}
                caseid={caseid}
                error={targetMissing}
                helperText={targetMissing
                  ? t('auto_create_folder.target_required', 'Folder path is required.')
                  : t(
                    'auto_create_folder.target_help',
                    'Select a parent folder, then add the new folder name. Dynamic tags are supported.',
                  )}
                label={t('auto_create_folder.target_label', 'Folder path')}
                onBrowse={handleBrowseClick}
                onChange={setTarget}
                placeholder={t(
                  'auto_create_folder.folder_path_placeholder',
                  'C:\\Output\\{year}\\{month}',
                )}
                tags={tags}
                value={target}
              />
            </CardContent>
          </Card>
        </Grid>
      </ActionSettingsLayout>

      {FolderBrowserComponent}
    </>
  );
};

export default React.memo(ActionsCreateFolder);
